import {
  OrderPreparationStep,
  OrderPreparationStepType,
  PanelConfiguration,
  PanelConfigurationDisplayMode,
  PickerProfile,
  SavePickerProfilePayload,
  StartupStrategy,
  StartupStrategyType,
  UiConfiguration,
  ValueExtender,
  Zone,
  ZoneIdentifier,
  ZoneStrategy,
} from '@ekkogmbh/apisdk';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PickerProfileUserInterfaceForm } from './PickerProfileUserInterfaceForm';
import { PickerProfileStartupForm } from './PickerProfileStartupForm';
import { ContentPanel, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import NamedCoordinateDescription from 'src/Common/Components/Stepper/NameCoordinateDescription';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import { ReactivePanelColumn } from 'src/Common/Components/Stacks';
import { StyledFormHeader } from 'src/Common/Components/Forms/StyledFormHeader';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { ZonePicker } from 'src/ZoneManagement/ZonePicker';
import useNotificationStrategy from 'src/ZoneManagement/Providers/NotificationStrategyProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { copy } from 'src/Common/Helper/FormHelper';

const defaultPanelConfig: PanelConfiguration = {
  displayMode: PanelConfigurationDisplayMode.DEFAULT,
  fieldConfig: { primary: '', secondary: '' },
  infos: {
    hidePrimaryField: false,
    hideSecondaryField: false,
    hideCoordinate: false,
    hideValue: false,
    coordinateParts: 1,
  },
};

const defaultUiOptions: UiConfiguration['options'] = {
  confirmStart: false,
};

export const valueExtenderToPreparationStep = (extender: ValueExtender): OrderPreparationStep => ({
  type: OrderPreparationStepType.ANNOTATION_SCAN,
  instruction: extender.description,
  configuration: {
    disabled: 'false', // TODO: add to apisdk? requires a more in-depth refacotring
    meta: 'value-extender', // helper property to disallow removal
    key: extender.annotation,
    validationRegex: extender.pattern,
    validationErrorMessage: '',
  },
});

const mergeValueExtendersIntoPreparationSteps = (steps: OrderPreparationStep[], zone: Zone) => {
  const preparationStepKeys =
    steps?.reduce((carry: string[], ops) => {
      if (ops.configuration?.key !== undefined) {
        carry.push(ops.configuration.key);
      }
      return carry;
    }, []) ?? [];

  const sourceExtenders = zone?.sourceValueExtenders ?? [];
  const destinationExtenders = zone?.destinationValueExtenders ?? [];

  const extenderSteps: OrderPreparationStep[] = [];
  sourceExtenders.concat(destinationExtenders).forEach((vext) => {
    const { annotation } = vext;
    if (!preparationStepKeys.includes(annotation)) {
      extenderSteps.push(valueExtenderToPreparationStep(vext));
    }
  });

  return extenderSteps.concat([...steps]);
};

interface Props {
  profile?: PickerProfile;
  closeHandler: () => void;
}

export const PickerProfilePanel = ({ profile, closeHandler }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { refresh } = usePagination();
  const { currentStep, enableNext, enableSave } = usePanel();

  const { notificationStrategy, setStrategies: setNotificationStrategy } = useNotificationStrategy();

  const [isSaving, startSaving] = useRequest();
  const isEditMode = useMemo(() => profile !== undefined, [profile]);

  const [name, setName] = useState<string>('');
  const [zone, setZone] = useState<Zone>();
  const [sourcePanel, setSourcePanel] = useState<PanelConfiguration>(defaultPanelConfig);
  const [destinationPanel, setDestinationPanel] = useState<PanelConfiguration>(defaultPanelConfig);
  const [uiOptions, setUiOptions] = useState<UiConfiguration['options']>(defaultUiOptions);
  const [mergeStrategy, setMergeStrategy] = useState<ZoneStrategy>();

  const [orderPreparationSteps, setOrderPreparationSteps] = useState<OrderPreparationStep[]>();
  const [startupStrategy, setStartupStrategy] = useState<StartupStrategy>({
    name: StartupStrategyType.SELECT,
    config: {},
  });

  const reset = useCallback(() => {
    setName(profile?.name ?? '');
    setSourcePanel(profile?.uiConfiguration.sourcePanel ?? defaultPanelConfig);
    setDestinationPanel(profile?.uiConfiguration.destinationPanel ?? defaultPanelConfig);
    setNotificationStrategy(profile?.notificationStrategy ?? undefined);
    setStartupStrategy(profile?.startupStrategy ?? { name: StartupStrategyType.SELECT, config: {} });
    setUiOptions(profile?.uiConfiguration.options ?? defaultUiOptions);
    setMergeStrategy(profile?.mergeStrategy ?? undefined);

    if (profile?.orderPreparationSteps !== undefined) {
      // step configurations are passed by reference, therefore we need to explicitly copy here
      // otherwise reset won't work properly
      setOrderPreparationSteps(copy(profile?.orderPreparationSteps));
    }

    setZone(profile?.zone);
  }, [profile]);

  useEffect(() => {
    reset();
  }, [reset]);

  useEffect(() => {
    if (zone === undefined) {
      return;
    }
    setOrderPreparationSteps(mergeValueExtendersIntoPreparationSteps(orderPreparationSteps ?? [], zone));
  }, [zone]);

  const payload: SavePickerProfilePayload | undefined = useMemo(() => {
    if (startupStrategy === undefined || sourcePanel === undefined || destinationPanel === undefined) {
      return undefined;
    }

    // fix weird type issue on startup strategy
    if (Array.isArray(startupStrategy.config) && startupStrategy.config.length === 0) {
      startupStrategy.config = {};
    }

    return {
      startupStrategy,
      orderPreparationSteps,
      uiConfiguration: {
        sourcePanel,
        destinationPanel,
        options: uiOptions,
      },
      notificationStrategy,
      mergeStrategy,
    };
  }, [
    startupStrategy,
    sourcePanel,
    destinationPanel,
    orderPreparationSteps,
    notificationStrategy,
    uiOptions,
    mergeStrategy,
  ]);

  useEffect(() => {
    enableSave(payload !== undefined && zone !== undefined && name !== '');

    switch (currentStep) {
      case 0:
        enableNext(name !== '' && zone !== undefined);
        break;
      default:
        enableNext(true);
    }
  }, [payload, zone, name]);

  const saveProfile = useCallback(() => {
    if (payload === undefined || zone === undefined) {
      return;
    }

    const zoneId: ZoneIdentifier = { name: zone!.name, coordinate: zone!.coordinate };
    startSaving(
      () => api.savePickerProfile(zoneId, name, payload, isEditMode),
      () => {
        refresh();
        closeHandler();
      },
    );
  }, [api, name, zone, payload, isEditMode, closeHandler]);

  const idStep = useMemo(
    () => ({
      title: 'Picker Profile',
      description: <NamedCoordinateDescription name={name} coordinate={zone?.coordinate} />,
      contentCallback: () => (
        <ReactivePanelColumn>
          <StyledFormHeader label={'Picker Profile'} />
          <StyledTextField
            disabled={isEditMode}
            type="text"
            label="Name"
            value={name}
            onChange={(e) => {
              setName(String(e.target.value));
            }}
          />
          <ZonePicker
            disabled={isEditMode}
            selected={zone}
            onChange={(zone: Zone | undefined) => {
              setZone(zone);
            }}
          />
        </ReactivePanelColumn>
      ),
    }),
    [zone, name, isEditMode],
  );

  const orderStep = useMemo(
    () => ({
      title: 'Order Settings',
      contentCallback: () =>
        zone ? (
          <PickerProfileStartupForm
            zone={zone}
            startupStrategy={startupStrategy}
            preparationSteps={orderPreparationSteps}
            uiOptions={uiOptions}
            mergeStrategy={mergeStrategy}
            onChangePreparationSteps={(steps) => setOrderPreparationSteps(steps)}
            onChangeStrategy={(strat) => setStartupStrategy(strat)}
            onChangeUiOptions={(options) => setUiOptions(options)}
            onChangeMergeStrategy={(strategy) => setMergeStrategy(strategy)}
          />
        ) : (
          <></>
        ),
    }),
    [startupStrategy, orderPreparationSteps, uiOptions, mergeStrategy, zone],
  );

  const uiStep = useMemo(
    () => ({
      title: 'User Inerface',
      contentCallback: () => (
        <PickerProfileUserInterfaceForm
          uiConfig={{ sourcePanel, destinationPanel }}
          onChange={(config) => {
            setSourcePanel(config.sourcePanel);
            setDestinationPanel(config.destinationPanel);
          }}
        />
      ),
    }),
    [sourcePanel, destinationPanel],
  );

  return (
    <ContentPanel
      isLoading={isSaving}
      initialStep={isEditMode ? 1 : 0}
      steps={[idStep, orderStep, uiStep]}
      onReset={reset}
      onSave={saveProfile}
      onCancel={closeHandler}
    />
  );
};
